import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';

function PasswordVisibilityIcon({ visible }) {
  return visible ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3 3L21 21" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10.58 10.58A2 2 0 0 0 13.41 13.41" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9.88 4.24A10.94 10.94 0 0 1 12 4c5 0 9.27 3.11 11 7.5a11.8 11.8 0 0 1-2.16 3.19" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M6.61 6.61A11.86 11.86 0 0 0 1 11.5C2.73 15.89 7 19 12 19a10.9 10.9 0 0 0 4.23-.84" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M1 12C2.73 7.61 7 4.5 12 4.5C17 4.5 21.27 7.61 23 12C21.27 16.39 17 19.5 12 19.5C7 19.5 2.73 16.39 1 12Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

/** Same-origin path only — blocks open redirects and protocol-relative URLs. */
function sanitizeRedirectPath(raw) {
  if (!raw || typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) return null;
  if (trimmed === '/login' || trimmed.startsWith('/login?')) return null;
  return trimmed;
}

export default function Login() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpRequested, setOtpRequested] = useState(false);
  const [loginMethod, setLoginMethod] = useState('email');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, requestOtp, verifyOtp } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const err = searchParams.get('error');
    if (err === 'google_denied') {
      setError(t('auth.googleSignInCancelled', 'Google sign-in was cancelled'));
      setSearchParams({}, { replace: true });
    } else if (err === 'invalid_or_expired_link') {
      setError(t('auth.invalidOrExpiredLink'));
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, t]);

  const handleGoogleSignIn = () => {
    window.location.href = api.getGoogleAuthUrl();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      let userData;
      if (loginMethod === 'email') {
        userData = await login(email, password);
      } else {
        await requestOtp(phoneNumber);
        setOtpRequested(true);
        setLoading(false);
        return;
      }
      const redirectTarget = sanitizeRedirectPath(searchParams.get('redirect'));
      if (redirectTarget) {
        navigate(redirectTarget);
      } else if (userData?.role === 'super_admin' || userData?.role === 'shop_admin') navigate('/admin');
      else if (userData?.role === 'shop_manager' || userData?.role === 'branch_manager') navigate('/manager');
      else navigate('/profile');
    } catch (err) {
      setError(err.message || t('auth.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-base)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem 1.5rem', fontFamily: "'Roboto', sans-serif", position: 'relative', overflow: 'hidden' }}>

      {/* Background watermark */}
      <div style={{ position: 'absolute', bottom: '5%', left: '50%', transform: 'translateX(-50%)', fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(4rem, 15vw, 10rem)', letterSpacing: '0.1em', color: 'var(--orange-glow)', whiteSpace: 'nowrap', pointerEvents: 'none', userSelect: 'none' }}>
        YALLA SALON
      </div>

      {/* Background glow */}
      <div style={{ position: 'absolute', top: '30%', left: '50%', transform: 'translate(-50%, -50%)', width: '600px', height: '600px', borderRadius: '50%', background: 'radial-gradient(circle, var(--orange-glow) 0%, transparent 70%)', pointerEvents: 'none' }} />

      <div style={{ width: '100%', maxWidth: '440px', position: 'relative', zIndex: 1 }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
          <Link to="/" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', marginBottom: '1.5rem' }}>
            <img
              src="/Logo-Yalla-Salon.png"
              alt="Yalla Salon"
              style={{ height: '70px', width: 'auto', mixBlendMode: 'screen' }}
            />
          </Link>
          <h1 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '2.5rem', letterSpacing: '0.08em', color: '#ffffff', marginBottom: '0.4rem' }}>
            {t('auth.welcomeBack')}
          </h1>
          <div style={{ width: '40px', height: '3px', background: 'var(--orange)', borderRadius: '2px', margin: '0 auto 0.75rem' }} />
          <p style={{ fontSize: '0.88rem', color: '#ffffff' }}>{t('auth.signInSubtitle')}</p>
        </div>

        {/* Card */}
        <div style={{ background: '#000000', border: '1px solid var(--border)', borderRadius: '12px', padding: '2.5rem', position: 'relative', overflow: 'hidden' }}>
          {/* Orange top bar */}
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(90deg, var(--orange-dark), var(--orange), var(--orange-light))' }} />

          {error && (
            <div style={{ marginBottom: '1.25rem', padding: '0.85rem 1rem', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '6px', color: '#fca5a5', fontSize: '0.85rem' }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', justifyContent: 'center' }}>
              <button
                type="button"
                onClick={() => { setLoginMethod('email'); setError(''); setOtpRequested(false); }}
                style={{ flex: 1, padding: '0.5rem', background: loginMethod === 'email' ? 'var(--orange)' : 'transparent', color: loginMethod === 'email' ? '#fff' : '#ffffff', border: '1px solid var(--border)', borderRadius: '6px', cursor: 'pointer', fontFamily: "'Oswald', sans-serif" }}
              >
                EMAIL
              </button>
              <button
                type="button"
                onClick={() => { setLoginMethod('phone'); setError(''); setOtpRequested(false); }}
                style={{ flex: 1, padding: '0.5rem', background: loginMethod === 'phone' ? 'var(--orange)' : 'transparent', color: loginMethod === 'phone' ? '#fff' : '#ffffff', border: '1px solid var(--border)', borderRadius: '6px', cursor: 'pointer', fontFamily: "'Oswald', sans-serif" }}
              >
                PHONE (OTP)
              </button>
            </div>

            {loginMethod === 'email' ? (
              <>
                <label style={{ display: 'block', fontFamily: "'Oswald', sans-serif", fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#ffffff', marginBottom: '0.5rem' }}>{t('auth.email')}</label>
                <input
                  id="email"
                  type="email"
                  placeholder={t('auth.placeholderEmail')}
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  className="barber-input login-input"
                  style={{ marginBottom: '1.25rem', color: '#ffffff' }}
                />

                <label style={{ fontFamily: "'Oswald', sans-serif", fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#ffffff', marginBottom: '0.5rem', display: 'block' }}>
                  {t('auth.password')}
                </label>
                <div style={{ position: 'relative', marginBottom: '0.6rem' }}>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t('auth.placeholderPassword')}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    className="barber-input login-input"
                    style={{ marginBottom: 0, paddingRight: '2.8rem', color: '#ffffff' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(prev => !prev)}
                    aria-label={showPassword ? t('auth.hidePassword', 'Hide password') : t('auth.showPassword', 'Show password')}
                    style={{ position: 'absolute', right: '0.8rem', top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', color: '#ffffff', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <PasswordVisibilityIcon visible={showPassword} />
                  </button>
                </div>
                <div style={{ marginBottom: '2rem' }}>
                  <Link to="/forgot-password" style={{ color: '#ffffff', textDecoration: 'none', textTransform: 'none', letterSpacing: 'normal', fontSize: '0.82rem' }}>
                    {t('auth.forgotPasswordLink', 'Forgot password?')}
                  </Link>
                </div>
              </>
            ) : (
              <>
                <label style={{ display: 'block', fontFamily: "'Oswald', sans-serif", fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#ffffff', marginBottom: '0.5rem' }}>{t('auth.phoneNumber', 'Phone Number')}</label>
                <input
                  id="phone"
                  type="tel"
                  placeholder="+1234567890"
                  value={phoneNumber}
                  onChange={e => setPhoneNumber(e.target.value)}
                  required
                  disabled={otpRequested}
                  className="barber-input login-input"
                  style={{ marginBottom: otpRequested ? '1.25rem' : '2rem', color: '#ffffff' }}
                />

                {otpRequested && (
                  <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.9)', marginBottom: '1.25rem', lineHeight: 1.5 }}>
                    {t('auth.verificationLinkSent')}
                  </p>
                )}
              </>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-orange"
              style={{ width: '100%', justifyContent: 'center', fontSize: '0.95rem', padding: '0.9rem', opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
            >
              {loading ? t('auth.signingIn') : (loginMethod === 'phone' ? (otpRequested ? t('auth.resendLink') : t('auth.sendVerificationLink')) : t('auth.signIn'))}
            </button>

            <div style={{ marginTop: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
              <span style={{ fontSize: '0.75rem', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('auth.or', 'or')}</span>
              <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            </div>
            <button
              type="button"
              onClick={handleGoogleSignIn}
              style={{ width: '100%', marginTop: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: '0.75rem 1rem', background: '#fff', color: '#333', border: '1px solid var(--border)', borderRadius: '8px', cursor: 'pointer', fontFamily: "'Oswald', sans-serif", fontSize: '0.9rem' }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {t('auth.signInWithGoogle', 'Sign in with Google')}
            </button>
          </form>
        </div>

        <p style={{ textAlign: 'center', fontSize: '0.85rem', marginTop: '1.5rem' }}>
          <span style={{ color: '#ffffff' }}>{t('auth.noAccount')}{' '}</span>
          <Link to="/signup" style={{ color: '#ffffff', textDecoration: 'none', fontWeight: 600, fontFamily: "'Oswald', sans-serif", letterSpacing: '0.05em' }}>
            {t('auth.createOneLink')}
          </Link>
        </p>
      </div>
    </div>
  );
}
